import { useCallback, useMemo, useState } from "react";
import {
  useBoardTasks,
  computeBoardOverviewMetrics,
} from "@onetasklist/shared";
import type { MetricKey } from "@onetasklist/shared/viewmodels/board/board-overview/boardOverviewMetrics.types";
import { useApi } from "@api/useApi";
import { useBoardFilters } from "@contexts/BoardFiltersContext";
import { useBoardId } from "@contexts/BoardContext";
import type {
  SidebarOverviewFilterItem,
  SidebarOverviewFiltersViewModel,
} from "./SidebarOverviewFilters.ViewModel.types";

/**
 * Order the rows appear in the pane. Active work first, then the things wrong
 * with it, then Done — which is the one row that is not active work at all.
 */
export const OVERVIEW_FILTER_KEYS: MetricKey[] = [
  "activeTasks",
  "openSubTasks",
  "overdueTasks",
  "atRiskTasks",
  "oldTasks",
  "avgAge",
  "doneTasks",
];

export const useSidebarOverviewFiltersViewModel =
  (): SidebarOverviewFiltersViewModel => {
    const api = useApi();
    const boardId = useBoardId();
    const { activeMetricFilters, toggleMetricFilter } = useBoardFilters();
    const [isSectionOpen, setIsSectionOpen] = useState(true);

    const { data: boardTasksData } = useBoardTasks(api.tasks, boardId ?? "", {
      enabled: !!boardId,
    });

    // Computed from every task on the board, not the filtered set: these are
    // the numbers you filter *by*, so narrowing them as you filter would make
    // each row describe the result of pressing it rather than the board.
    const metrics = useMemo(
      () => computeBoardOverviewMetrics(boardTasksData?.tasks ?? [], new Date()),
      [boardTasksData],
    );

    const filters = useMemo<SidebarOverviewFilterItem[]>(
      () =>
        OVERVIEW_FILTER_KEYS.map((key) => ({
          key,
          value: metrics[key],
          isSelected: activeMetricFilters.includes(key),
          isEmpty: metrics[key] === 0,
        })),
      [metrics, activeMetricFilters],
    );

    const toggleSection = useCallback(() => {
      setIsSectionOpen((value) => !value);
    }, []);

    return {
      filters,
      isSectionOpen,
      toggleFilter: toggleMetricFilter,
      toggleSection,
    };
  };
